import React, {useContext} from 'react';
import {Controller} from 'react-hook-form';
import {FormFieldDesignContext} from '@src/contexts/FormFieldDesignContext';
import CustomSelect, {defaultComponents} from '@src/components/Fields/CustomSelect';
import classnames from '@src/helpers/classnames';
import {FormFieldItemPropsContext} from '@src/contexts/FormFieldItemPropsContext';
import {IFormFieldItemPropsContext} from '@src/@interfaces/contexts/IFormFieldItemPropsContext';
import {IOrderModel} from '@src/@interfaces/models/Order/IOrderModel';
import FormErrorMessage from '@src/components/Form/FormErrorMessage';
import {IListColumnProps} from '@src/@interfaces/components/List/IListColumnProps';
import FieldLayout from './extra_row/FieldLayout';
import app from '@src/helpers/app';
import {CustomSelectProps} from '@src/@types/components/Fields/CustomSelect';
import {FormFieldOption} from '@src/@types/components/Form';
import {LocaleContext} from '@src/contexts/LocaleContext';
import trans from '@src/helpers/trans';

const emptyArray: never[] = [];

function CustomOption(props: React.ComponentProps<typeof defaultComponents.Option>) {
    const option = props.data as FormFieldOption & { id: string | number, tech_processes?: any[] };
    const hasTechProcess = (option.tech_processes?.length ?? 0) > 0;
    const {locale} = useContext(LocaleContext);

    const tooltip = hasTechProcess ?
            'OrderForm.products.has-tech-process-tooltip' :
            'OrderForm.products.does-not-have-tech-process-tooltip';

    return <defaultComponents.Option {...props}>
        <i
                className={classnames('fa fa-wrench me-2', {
                    'opacity-0': !hasTechProcess,
                    'opacity-50': hasTechProcess,
                })}
                title={trans(tooltip, locale)}
        />

        {props.children}
    </defaultComponents.Option>;
}

const customComponents: CustomSelectProps<false>['customComponents'] = {
    Option: CustomOption,
};

function ProductActualField({rowIndex: index, isDisabled}: {
    rowIndex: number,
    isDisabled?: boolean,
}) {
    const {
        control,
        fieldName,
        modelOptions = {},
        model = {},
        forceDisabled,
        forceReadonly
    } = useContext<IFormFieldItemPropsContext<IOrderModel> | null>(FormFieldItemPropsContext) ?? {};
    const [design] = useContext(FormFieldDesignContext);

    const dot = app('dot-accessor');
    const mustBeDisabled = Boolean(dot.get(model, `${fieldName}.${index}.id`, null));

    return <>
        <Controller
                control={control}
                name={`${fieldName}.${index}.product_id`}
                render={({field: {name, value, onChange, onBlur}, fieldState: {error}}) => {
                    return <CustomSelect
                            label="OrderForm.products.fields.product"
                            className={classnames({
                                'is-invalid': Boolean(error)
                            })}
                            name={name}
                            options={modelOptions.products ?? emptyArray}
                            readOnly={design.fieldsAreReadOnly || forceReadonly}
                            disabled={mustBeDisabled || isDisabled || design.fieldsAreDisabled || forceDisabled}
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            menuPortalTarget={document.body}
                            customComponents={customComponents}
                    />;
                }}
        />

        <FormErrorMessage fieldName={`${fieldName}.${index}.product_id`}/>
    </>;
}

export function ProductIndependentField(props: {
    rowIndex: number,
    isDisabled?: boolean,
}) {
    return <FieldLayout
            id={null}
            label="OrderForm.products.fields.product"
            className="mb-1"
    >
        <ProductActualField {...props}/>
    </FieldLayout>;
}

export function ProductField({rowIndex: index}: IListColumnProps) {
    return <ProductActualField rowIndex={index}/>;
}
